import { useEffect, useRef, useState } from "react";
import { ImagePlus, Loader2, Printer, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { stickerInfo } from "@/lib/stickers";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StickerLabel from "@/components/stickers/StickerLabel";
import { labelOrigin } from "@/lib/stickerDesign";

const PER_SHEET = 30;
const LINES = [["logo", "Logo"], ["biz", "Business name"], ["phones", "Phone numbers"], ["number", "Sticker number"], ["service", "Service date"], ["tech", "Tech"], ["due", "Next due"]];
const DEFAULTS = Object.fromEntries(LINES.map(([k]) => [k, true]));

function Sticker({ s, biz, opt, logo }) {
  return (
    <div className="sticker" data-testid="sticker">
      {opt.logo && logo && <img src={logo} alt="" className="logo" />}
      <div className="txt">
        {opt.biz && <p className="biz">{biz.company_name}</p>}
        {opt.phones && <p className="bizline">{[biz.phone, biz.toll_free].filter(Boolean).join(" · ")}</p>}
        {opt.number && <p className="num">No. {s.number}</p>}
        {(opt.service || opt.tech) && <div className="row">{opt.service && <span>Serviced: {s.service || "______"}</span>}{opt.tech && <span>Tech: {s.tech || "___"}</span>}</div>}
        {opt.due && <p className="due">Next due: {s.next || "__________"}</p>}
      </div>
    </div>
  );
}

function Options({ opt, setOpt, hasLogo, isAdmin, onUpload, onRemove, busy }) {
  const file = useRef(null);
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm" data-testid="sticker-options">
      {LINES.map(([k, label]) => (
        <label key={k} className="flex items-center gap-1.5 cursor-pointer">
          <Checkbox checked={opt[k]} onCheckedChange={(v) => setOpt({ ...opt, [k]: !!v })} data-testid={`sticker-opt-${k}`} className="border-neutral-500" /> {label}
        </label>
      ))}
      {isAdmin && <>
        <input ref={file} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => { e.target.files[0] && onUpload(e.target.files[0]); e.target.value = ""; }} data-testid="sticker-logo-input" />
        <Button size="sm" variant="outline" className="bg-white text-black" onClick={() => file.current.click()} disabled={busy} data-testid="sticker-logo-upload">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} {hasLogo ? "Change logo" : "Upload logo"}
        </Button>
        {hasLogo && <Button size="sm" variant="ghost" className="text-black" onClick={onRemove} disabled={busy} data-testid="sticker-logo-remove"><X size={14} /> Remove logo</Button>}
      </>}
    </div>
  );
}

export default function StickerPrint() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState(null);
  const [biz, setBiz] = useState({});
  const [skip, setSkip] = useState(0);
  const [error, setError] = useState("");
  const [logo, setLogo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [designs, setDesigns] = useState([]);
  const [designId, setDesignId] = useState(() => localStorage.getItem("sticker-design") || "");
  const design = designs.find((d) => d.id === designId) || null;
  const perSheet = design ? design.sheet.cols * design.sheet.rows : PER_SHEET;
  const screenOutline = true;
  useEffect(() => { api.get("/branding/sticker-designs").then((r) => setDesigns(r.data)).catch(() => {}); }, []);
  const [opt, setOptState] = useState(() => ({ ...DEFAULTS, ...JSON.parse(localStorage.getItem("sticker-options") || "{}") }));
  const setOpt = (o) => { setOptState(o); localStorage.setItem("sticker-options", JSON.stringify(o)); };

  const loadLogo = () => api.get("/branding/logo", { responseType: "blob" })
    .then((r) => setLogo((old) => { old && URL.revokeObjectURL(old); return URL.createObjectURL(r.data); }))
    .catch(() => setLogo(null));

  useEffect(() => {
    loadLogo();
    const stops = JSON.parse(localStorage.getItem("sticker-print") || "[]");
    if (!stops.length) { setError("Pick records in a table and click Print stickers first."); return; }
    Promise.all([api.post("/route-setup/records", { stops }), api.get("/invoice-settings")]).then(([r, s]) => {
      setBiz(s.data);
      setItems(stops.flatMap((st) => {
        const rec = r.data.records[st.record_id];
        if (!rec) return [];
        const info = stickerInfo(rec.data);
        return info.numbers.map((number) => ({ ...info, number }));
      }));
    }).catch((e) => setError(formatError(e)));
  }, []);

  const upload = async (f) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/branding/logo", fd);
      await loadLogo();
      toast.success("Logo saved");
    } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  };
  const remove = async () => {
    setBusy(true);
    try { await api.delete("/branding/logo"); setLogo(null); toast.success("Logo removed"); } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  };

  const cells = items ? [...Array(Math.max(0, Math.min(perSheet - 1, skip))).fill(null), ...items] : [];
  const sheets = [];
  for (let i = 0; i < cells.length; i += perSheet) sheets.push(cells.slice(i, i + perSheet));

  return (
    <div className="min-h-screen bg-neutral-200 text-black" data-testid="sticker-print-page">
      <style>{`
        @page { size: letter; margin: 0; }
        .sheet { width: 8.5in; height: 11in; padding: 0.5in 0.1875in 0; background: #fff; display: grid; grid-template-columns: repeat(3, 2.625in); column-gap: 0.125in; grid-template-rows: repeat(10, 1in); grid-auto-flow: column; margin: 0 auto 0.3in; box-sizing: border-box; }
        .sticker, .blank { width: 2.625in; height: 1in; box-sizing: border-box; padding: 0.06in 0.14in; overflow: hidden; font-family: Arial, sans-serif; }
        .sticker { display: flex; align-items: center; gap: 0.08in; }
        .sticker .logo { max-width: 0.75in; max-height: 0.85in; object-fit: contain; flex-shrink: 0; }
        .sticker .txt { flex: 1; min-width: 0; }
        .sticker .biz { font-weight: 700; font-size: 9pt; line-height: 1.1; }
        .sticker .bizline { font-size: 6.5pt; line-height: 1.2; }
        .sticker .num { font-weight: 800; font-size: 12pt; line-height: 1.3; }
        .sticker .row { display: flex; justify-content: space-between; gap: 4px; font-size: 7.5pt; line-height: 1.25; }
        .sticker .due { font-size: 8pt; font-weight: 700; line-height: 1.25; }
        .dsheet { position: relative; background: #fff; margin: 0 auto 0.3in; overflow: hidden; }
        @media screen { .sticker { outline: 1px dashed #bbb; } }
        @media print { .sheet, .dsheet { margin: 0; page-break-after: always; } .no-print { display: none !important; } body { background: #fff; } [data-testid=sticker-label] { outline: none !important; } }
      `}</style>
      <div className="no-print space-y-2 border-b bg-white px-6 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium" data-testid="sticker-count">{items ? `${items.length} stickers · ${sheets.length} sheet${sheets.length === 1 ? "" : "s"}` : ""}</span>
          <Select value={designId || "standard"} onValueChange={(v) => { const x = v === "standard" ? "" : v; setDesignId(x); localStorage.setItem("sticker-design", x); }}>
            <SelectTrigger className="w-60 h-8 bg-white text-black" data-testid="sticker-design-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-card">
              <SelectItem value="standard">Standard (Avery 5160)</SelectItem>
              {designs.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <label className="text-sm flex items-center gap-2">Skip used labels
            <Input type="number" min={0} max={perSheet - 1} value={skip} onChange={(e) => setSkip(Number(e.target.value) || 0)} className="w-20 h-8 bg-white" data-testid="sticker-skip-input" />
          </label>
          <Button onClick={() => window.print()} disabled={!items?.length} data-testid="sticker-print-button"><Printer size={15} /> Print</Button>
          <span className="text-xs text-neutral-500">Print at 100% / "Actual size", no scaling.</span>
        </div>
        {!design && <Options opt={opt} setOpt={setOpt} hasLogo={!!logo} isAdmin={isAdmin} onUpload={upload} onRemove={remove} busy={busy} />}
      </div>
      {error && <p className="p-6 text-red-600" data-testid="sticker-error">{error}</p>}
      {!items && !error && <Loader2 className="m-6 animate-spin" />}
      {items && !items.length && <p className="p-6" data-testid="sticker-empty">None of the picked records have sticker numbers.</p>}
      <div className="py-6 print:py-0">
        {design ? sheets.map((sh, i) => (
          <div className="dsheet" key={i} style={{ width: `${design.sheet.page_w}in`, height: `${design.sheet.page_h}in` }}>
            {sh.map((s, k) => {
              if (!s) return null;
              const o = labelOrigin(design.sheet, k);
              return <div key={k} style={{ position: "absolute", left: `${o.left}in`, top: `${o.top}in` }}><StickerLabel sheet={design.sheet} elements={design.elements} decor={design.decor} data={s} biz={biz} logo={logo} outline={screenOutline} /></div>;
            })}
          </div>
        )) : sheets.map((sh, i) => (
          <div className="sheet" key={i}>{sh.map((s, k) => (s ? <Sticker key={k} s={s} biz={biz} opt={opt} logo={logo} /> : <div key={k} className="blank" />))}</div>
        ))}
      </div>
    </div>
  );
}
